import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  useTheme,
  alpha,
  Stepper,
  Step,
  StepLabel,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ShoppingCart as CartIcon,
} from '@mui/icons-material';

const OrdenesCompra = () => {
  const theme = useTheme();
  const [openDialog, setOpenDialog] = useState(false);

  const workflowSteps = ['Solicitud', 'Aprobación', 'Orden', 'Recepción', 'Factura'];

  const ordenesData = [
    { id: 1, numero: 'OC-2026-0045', proveedor: 'Suministros Oil', fecha: '03/02/2026', monto: 850000, estado: 2, urgente: false },
    { id: 2, numero: 'OC-2026-0044', proveedor: 'Transporte Petrolero', fecha: '02/02/2026', monto: 1250000, estado: 1, urgente: true },
    { id: 3, numero: 'OC-2026-0043', proveedor: 'Servicios HSE SRL', fecha: '01/02/2026', monto: 380000, estado: 3, urgente: false },
    { id: 4, numero: 'OC-2026-0042', proveedor: 'Equipos y Herramientas', fecha: '30/01/2026', monto: 520000, estado: 4, urgente: false },
  ];

  const getEstadoText = (estado) => {
    switch (estado) {
      case 0: return 'Solicitud';
      case 1: return 'Pend. Aprobación';
      case 2: return 'Aprobada';
      case 3: return 'Recibida';
      case 4: return 'Facturada';
      default: return 'Desconocido';
    }
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 0: return 'default';
      case 1: return 'warning';
      case 2: return 'info';
      case 3: return 'success';
      case 4: return 'success';
      default: return 'default';
    }
  };

  return (
    <Box className="fade-in">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="primary">
            Órdenes de Compra
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Workflow: Solicitud → Aprobación → Orden → Recepción → Factura
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)}>
          Nueva Solicitud
        </Button>
      </Box>

      {/* Resumen */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h5" fontWeight={700} color="warning.main">4</Typography>
              <Typography variant="caption" color="text.secondary">Pendientes</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h5" fontWeight={700} color="info.main">5</Typography>
              <Typography variant="caption" color="text.secondary">Aprobadas</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h5" fontWeight={700} color="success.main">3</Typography>
              <Typography variant="caption" color="text.secondary">Recibidas</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h5" fontWeight={700} color="primary">$3M</Typography>
              <Typography variant="caption" color="text.secondary">En Proceso</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={2.4}>
          <Card sx={{ bgcolor: alpha(theme.palette.error.main, 0.05) }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h5" fontWeight={700} color="error.main">1</Typography>
              <Typography variant="caption" color="text.secondary">Urgentes</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Órdenes */}
      <Grid container spacing={3}>
        {ordenesData.map((orden) => (
          <Grid item xs={12} md={6} key={orden.id}>
            <Card sx={{ border: orden.urgente ? `2px solid ${theme.palette.error.main}` : 'none' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h6" fontWeight={600}>{orden.numero}</Typography>
                      {orden.urgente && <Chip label="URGENTE" size="small" color="error" />}
                    </Box>
                    <Typography variant="body2" color="text.secondary">{orden.proveedor}</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h6" fontWeight={700} color="primary">
                      ${orden.monto.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{orden.fecha}</Typography>
                  </Box>
                </Box>

                <Stepper activeStep={orden.estado} alternativeLabel sx={{ mb: 2 }}>
                  {workflowSteps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Chip label={getEstadoText(orden.estado)} color={getEstadoColor(orden.estado)} size="small" />
                  <Box>
                    {orden.estado === 1 && (
                      <>
                        <IconButton size="small" color="success"><CheckIcon /></IconButton>
                        <IconButton size="small" color="error"><CloseIcon /></IconButton>
                      </>
                    )}
                    <IconButton size="small"><ViewIcon /></IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Nueva Solicitud de Compra</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Proveedor</InputLabel>
                <Select label="Proveedor">
                  <MenuItem value="1">Suministros Oil</MenuItem>
                  <MenuItem value="2">Transporte Petrolero</MenuItem>
                  <MenuItem value="3">Servicios HSE SRL</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Centro de Costo</InputLabel>
                <Select label="Centro de Costo">
                  <MenuItem value="1">Pozo XY-123</MenuItem>
                  <MenuItem value="2">Pozo AB-456</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Descripción" multiline rows={2} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Monto Estimado" type="number" />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Prioridad</InputLabel>
                <Select label="Prioridad">
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="urgente">Urgente</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Justificación" multiline rows={2} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={() => setOpenDialog(false)}>Enviar Solicitud</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrdenesCompra;
